import { Editor, Extension, mergeAttributes, Node } from '@tiptap/core';
import Table, { createTable } from '@tiptap/extension-table';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { TextSelection } from '@tiptap/pm/state';
import { blockSpacing } from './prompt-blocks';

/*
 * Tables for the prompt editor, stored as Markdown tables (see
 * prompt-markdown.ts). A Markdown table cell holds one line of text, so cells
 * hold exactly one paragraph (bold, italic, code, {{variables}}, ...; no
 * lists or headings), and tables only appear at the top level of the prompt,
 * not inside lists or quotes.
 */

// Spacing around a cell's text in the stored Markdown, e.g. "| Field      |",
// kept so an existing table loads and saves back byte for byte
const cellPadding = {
  padBefore: { default: ' ', rendered: false },
  padAfter: { default: ' ', rendered: false },
};

export const PROMPT_TABLE_GROUP = 'promptTable';

export const PromptTable = Table.extend({
  group: PROMPT_TABLE_GROUP,
  addAttributes() {
    return {
      ...this.parent?.(),
      // The stored separator row ("| --- | :---: |"), kept for the same reason
      separator: { default: null, rendered: false },
      ...blockSpacing,
    };
  },
}).configure({ resizable: false, allowTableNodeSelection: false });

export const PromptTableRow = TableRow;

export const PromptTableHeader = TableHeader.extend({
  content: 'paragraph',
  addAttributes() {
    return { ...this.parent?.(), ...cellPadding };
  },
});

export const PromptTableCell = Node.create({
  name: 'tableCell',
  content: 'paragraph',
  tableRole: 'cell',
  isolating: true,

  addAttributes() {
    return {
      colspan: { default: 1 },
      rowspan: { default: 1 },
      colwidth: { default: null },
      ...cellPadding,
    };
  },

  parseHTML() {
    return [{ tag: 'td' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['td', mergeAttributes(HTMLAttributes), 0];
  },
});

// A cell holds one line, so Enter moves on instead of doing nothing: to the
// next cell, adding a row after the last one (the same as Tab), with the
// cursor at the end of the cell rather than selecting its text
const PromptTableKeys = Extension.create({
  name: 'promptTableKeys',
  // Before the default Enter (split block), which can't split a cell
  priority: 1000,

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { editor } = this;
        if (!editor.isActive('tableCell') && !editor.isActive('tableHeader')) {
          return false;
        }
        const moved =
          editor.commands.goToNextCell() ||
          editor.chain().addRowAfter().goToNextCell().run();
        if (moved) editor.commands.setTextSelection(editor.state.selection.to);
        return true;
      },
    };
  },
});

/**
 * Insert a 3x3 table with a header row after the block the cursor is in (or
 * in place of an empty line), so a heading, list or quote is never cut in two.
 */
export const insertPromptTable = (editor: Editor) =>
  editor
    .chain()
    .focus()
    .command(({ tr, state, dispatch }) => {
      const table = createTable(state.schema, 3, 3, true);
      const { $from } = state.selection;
      let from = $from.pos;
      let to = $from.pos;
      if ($from.depth > 0) {
        const block = $from.node(1);
        const isEmptyLine =
          block.type.name === 'paragraph' && block.content.size === 0;
        to = $from.after(1);
        from = isEmptyLine ? $from.before(1) : to;
      }
      if (dispatch) {
        tr.replaceWith(from, to, table);
        // Cursor in the first header cell
        tr.setSelection(TextSelection.near(tr.doc.resolve(from + 1)));
        tr.scrollIntoView();
      }
      return true;
    })
    .run();

export const PROMPT_TABLE_EXTENSIONS = [
  PromptTable,
  PromptTableRow,
  PromptTableHeader,
  PromptTableCell,
  PromptTableKeys,
];
